import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useImageContext } from "../../contexts/ImageContext";
import { clearEditorSession } from "../../state/editorStore";
import {
  ENTRY_SOURCE,
  clearToolPrepSession,
  isDocumentToolPrepKey,
  isToolPrepKey,
} from "../../utils/editorEntrySession";
import { ToolPrepCloseContext } from "../tools/shared/Popup/ToolPrepPopup";
import ToolPrepSwitch from "../tools/shared/Popup/ToolPrepSwitch";
import { adoptBlobUrl } from "../../utils/adoptBlobUrl";
import "../../pages/tools/ToolPrepWorkspace.css";

/** Copy blob URLs before the tool unmounts and revokes them. */
async function keepToolResultBlobs(result) {
  if (!result || typeof result !== "object") return result;
  const imageUrl = result.imageUrl
    ? await adoptBlobUrl(result.imageUrl)
    : result.imageUrl;
  const originalImageUrl = result.originalImageUrl
    ? await adoptBlobUrl(result.originalImageUrl)
    : result.originalImageUrl;
  const watermark = result.watermark?.logoUrl
    ? {
        ...result.watermark,
        logoUrl: await adoptBlobUrl(result.watermark.logoUrl),
      }
    : result.watermark;
  return { ...result, imageUrl, originalImageUrl, watermark };
}

/**
 * In-page tool prep overlay for Dashboard / Tools catalog.
 * Keeps the host page mounted (no remount / refetch on close).
 */
const DashboardToolPrepOverlay = ({ toolKey = null, onClose = () => {} }) => {
  const navigate = useNavigate();
  const { selectedFile, inputPreview, clearImage } = useImageContext();

  const handleCancel = useCallback(() => {
    clearToolPrepSession();
    try {
      clearImage?.();
    } catch {
      // ignore
    }
    try {
      clearEditorSession?.();
    } catch {
      // ignore
    }
    onClose();
  }, [clearImage, onClose]);

  const handleDone = useCallback(
    async (result) => {
      if (result?.exported || result?.stayOpen) return;
      if (isDocumentToolPrepKey(toolKey)) return;
      if (!result) return;
      const kept = await keepToolResultBlobs(result);
      clearToolPrepSession();
      onClose();
      navigate("/editor", {
        state: {
          entrySource: ENTRY_SOURCE.NORMAL,
          deferBackgroundRemoval: true,
          processedImage: kept.imageUrl || null,
          toolPrepApply: { kind: toolKey, result: kept },
        },
      });
    },
    [navigate, onClose, toolKey]
  );

  if (!isToolPrepKey(toolKey)) return null;

  return (
    <ToolPrepCloseContext.Provider value="dashboard">
      <div className="tool-prep-workspace tool-prep-workspace--overlay">
        <ToolPrepSwitch
          toolKey={toolKey}
          originalImageUrl={inputPreview || null}
          imageFile={selectedFile}
          onDone={handleDone}
          onCancel={handleCancel}
        />
      </div>
    </ToolPrepCloseContext.Provider>
  );
};

export default DashboardToolPrepOverlay;
